import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Store,
  Settings2,
  Zap,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Trash2,
  MousePointer2,
  RefreshCw,
  Split,
  Link as LinkIcon,
  ShieldAlert,
  LifeBuoy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { brandingConfig } from '@/config/branding';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { FlowStep, StepType } from '../types';
import { STEP_ICONS, STEP_TYPE_LABELS } from '../constants/stepStyles';
import { trpc } from '@/lib/api/trpc';
import toast from 'react-hot-toast';
import { CollapsibleSection, SectionGroupHeader } from './CollapsibleSection';
import { useAttentionTarget } from '@/hooks/use-attention-guide';
import { cn } from '@/lib/utils';
import { ShopifyPickButtonLink } from './ShopifyPickButtonLink';

interface Selector {
  xpath: string;
  label: string;
  config?: unknown;
}

interface ShopifyEditorPanelProps {
  step: FlowStep;
  storeId: string;
  funnelId: string;
  templatesWithIntegrationStatus?: any[];
  onClose: () => void;
  onChange: (step: FlowStep) => void;
  onSave?: (updatedStep: FlowStep) => Promise<void>;
  onPreview?: () => void;
}

interface ConfigFormData {
  enableSelector: boolean;
  enableGenericBuyNow: boolean;
  enableBuyNowButtonDetector: boolean;
  enableCheckoutButtonDetector: boolean;
  enableKachingBundle: boolean;
  enableShopifyCartPreloader: boolean;
  shopifyCheckoutSplitRatio: number;
  buyNowButtonDetectorUrlPatterns: string;
  buyNowButtonDetectorExcludeUrlPatterns: string;
  checkoutButtonDetectorExcludeUrlPatterns: string;
}

const DEFAULT_CONFIG: ConfigFormData = {
  enableSelector: true,
  enableGenericBuyNow: true,
  enableBuyNowButtonDetector: true,
  enableCheckoutButtonDetector: true,
  enableKachingBundle: true,
  enableShopifyCartPreloader: false,
  shopifyCheckoutSplitRatio: 100,
  buyNowButtonDetectorUrlPatterns: '',
  buyNowButtonDetectorExcludeUrlPatterns: '',
  checkoutButtonDetectorExcludeUrlPatterns: '',
};

const INTEGRATION_LOGO = '/integrations/shopify-icon.svg';

const parsePatterns = (value: string): string[] =>
  value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Shopify Store Editor Panel - Configuration interface for TgdMapper script
 */
export function ShopifyEditorPanel({
  step,
  storeId,
  funnelId,
  templatesWithIntegrationStatus,
  onClose,
  onChange,
  onSave,
  onPreview,
}: ShopifyEditorPanelProps) {
  const [config, setConfig] = useState<ConfigFormData>(DEFAULT_CONFIG);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { ref: installSectionRef, isHighlighted: installHighlighted } = useAttentionTarget<HTMLDivElement>('shopify-install', { duration: 2500 });
  const { storeRoutes } = useStoreNavigate();

  const {
    data: databaseSelectors,
    isLoading: isLoadingSelectors,
    refetch: refetchSelectors,
  } = trpc.shopifyScript.getDatabaseSelectors.useQuery(
    { storeId },
    { enabled: true, refetchInterval: false },
  );

  const {
    data: scriptStatus,
    isLoading: isCheckingScript,
    refetch: refetchScriptStatus,
  } = trpc.shopifyScript.checkScript.useQuery(
    { storeId, funnelId, stepId: step.id },
    { enabled: true, retry: false },
  );

  const [auditExpanded, setAuditExpanded] = useState(false);
  const {
    data: themeAudit,
    isLoading: isAuditing,
    isFetching: isAuditFetching,
    refetch: refetchAudit,
  } = trpc.shopifyScript.auditTheme.useQuery(
    { storeId },
    {
      // Audit is moderately expensive (~5-10 Shopify API calls). Only run
      // when the script is actually injected so we have signal worth
      // surfacing, and let merchants refresh it manually.
      enabled: !!scriptStatus?.isInjected,
      retry: false,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const shopifyAvailable = templatesWithIntegrationStatus?.some(
    (t: any) =>
      t.id === "shopify-default" &&
      t.integrationStatus?.connected
  ) ?? false;

  const wooAvailable = templatesWithIntegrationStatus?.some(
    (t: any) =>
      t.id === "woocommerce-default" &&
      t.integrationStatus?.connected
  ) ?? false;

  const updateScriptMutation = trpc.shopifyScript.updateScript.useMutation({
    onSuccess: async (result) => {
      toast.success(result.message);
      setHasUnsavedChanges(false);

      toast.loading('Verifying installation...', { id: 'shopify-verify' });
      await new Promise((resolve) => setTimeout(resolve, 3000));

      await refetchScriptStatus();
      await refetchSelectors();
      toast.dismiss('shopify-verify');
      toast.success('Installation verified');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update script');
    },
  });

  const removeScriptMutation = trpc.shopifyScript.removeScript.useMutation({
    onSuccess: async (result) => {
      toast.success(result.message);

      toast.loading('Verifying removal...', { id: 'shopify-verify' });
      await new Promise((resolve) => setTimeout(resolve, 3000));

      await refetchScriptStatus();
      toast.dismiss('shopify-verify');

      const updatedStep = {
        ...step,
        config: { ...step.config, url: undefined },
        metadata: { ...step.metadata, shopifyUrl: undefined },
      };
      onChange(updatedStep);

      if (onSave) {
        await onSave(updatedStep);
        toast.success('Funnel saved - Shopify URL removed');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove script');
    },
  });

  const deleteSelectorMutation = trpc.shopifyScript.deleteSelector.useMutation({
    onSuccess: async () => {
      toast.success('Selector deleted from database');
      await refetchSelectors();

      toast.loading('Updating script...', { id: 'update-script' });
      updateScriptMutation.mutate(
        { storeId, config: buildScriptConfig() },
        {
          onSuccess: () => {
            toast.dismiss('update-script');
            toast.success('Script updated - selector removed from Shopify');
          },
          onError: (error) => {
            toast.dismiss('update-script');
            toast.error('Failed to update script: ' + (error.message || 'Unknown error'));
          },
        },
      );
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete selector');
    },
  });

  const isInjected = scriptStatus?.isInjected ?? false;
  const isLoading = isCheckingScript || updateScriptMutation.isPending || removeScriptMutation.isPending;

  const pendingSelectorCount = useMemo(() => {
    if (!databaseSelectors || !scriptStatus?.config) return 0;
    const scriptXpaths = new Set(scriptStatus.config.selectors?.map((s) => s.xpath) ?? []);
    return databaseSelectors.filter((s: Selector) => !scriptXpaths.has(s.xpath)).length;
  }, [databaseSelectors, scriptStatus?.config]);

  const hasNewSelectors = pendingSelectorCount > 0;

  const buildScriptConfig = useCallback(
    () => ({
      funnelId,
      stepId: step.id,
      enableShopifyIntegration: true,
      enableSelector: config.enableSelector,
      enableGenericBuyNow: config.enableGenericBuyNow,
      enableBuyNowButtonDetector: config.enableBuyNowButtonDetector,
      enableCheckoutButtonDetector: config.enableCheckoutButtonDetector,
      enableKachingBundle: config.enableKachingBundle,
      enableShopifyCartPreloader: config.enableShopifyCartPreloader,
      shopifyCheckoutSplitRatio: config.shopifyCheckoutSplitRatio,
      mockMode: false,
      buyNowButtonDetectorUrlPatterns: parsePatterns(config.buyNowButtonDetectorUrlPatterns),
      buyNowButtonDetectorExcludeUrlPatterns: parsePatterns(config.buyNowButtonDetectorExcludeUrlPatterns),
      checkoutButtonDetectorExcludeUrlPatterns: parsePatterns(
        config.checkoutButtonDetectorExcludeUrlPatterns,
      ),
    }),
    [funnelId, step.id, config],
  );

  const handleConfigChange = useCallback((field: keyof ConfigFormData, value: unknown) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  }, []);

  const handleSaveChanges = useCallback(() => {
    if (scriptStatus?.isConflict && scriptStatus.conflictDetails) {
      const confirmMessage =
        `⚠️ WARNING: Script Override Detected\n\n` +
        `An existing TagadaPay script is currently installed for:\n` +
        `${scriptStatus.conflictDetails.currentStoreId ? `• Another store: ${scriptStatus.conflictDetails.currentStoreId}\n` : ''}` +
        `${scriptStatus.conflictDetails.currentFunnelId ? `• Funnel: ${scriptStatus.conflictDetails.currentFunnelId}\n` : ''}` +
        `${scriptStatus.conflictDetails.currentStepId ? `• Step: ${scriptStatus.conflictDetails.currentStepId}\n` : ''}` +
        `\nInstalling this configuration will REMOVE the old script and replace it with the new one.\n\n` +
        `⚠️ Only ONE TagadaPay script can be active at a time.\n\n` +
        `Do you want to proceed and override the existing configuration?`;

      if (!window.confirm(confirmMessage)) return;
    }

    updateScriptMutation.mutate(
      { storeId, config: buildScriptConfig() },
      {
        onSuccess: async () => {
          if (scriptStatus?.shopUrl) {
            const updatedStep = {
              ...step,
              config: { ...step.config, url: `https://${scriptStatus.shopUrl}` },
              metadata: { ...step.metadata, shopifyUrl: scriptStatus.shopUrl },
            };
            onChange(updatedStep);

            if (onSave) {
              await onSave(updatedStep);
              toast.success('Funnel saved with Shopify URL');
            }
          }
        },
      },
    );
  }, [scriptStatus, storeId, buildScriptConfig, updateScriptMutation, step, onChange, onSave]);

  const handleRemoveScript = useCallback(() => {
    if (
      window.confirm(
        'Are you sure you want to remove the TagadaPay integration script from your Shopify theme? This will stop all redirects to TagadaPay checkout.',
      )
    ) {
      removeScriptMutation.mutate({ storeId });
    }
  }, [removeScriptMutation, storeId]);

  const handleDeleteSelector = useCallback(
    (xpath: string) => {
      if (
        window.confirm(
          'Delete this selector?\n\nThis will:\n1. Remove it from the database\n2. Automatically update the Shopify script to remove it from the storefront',
        )
      ) {
        deleteSelectorMutation.mutate({ storeId, xpath });
      }
    },
    [deleteSelectorMutation, storeId],
  );

  useEffect(() => {
    if (scriptStatus?.config) {
      setConfig({
        enableSelector: scriptStatus.config.enableSelector ?? true,
        enableGenericBuyNow: scriptStatus.config.enableGenericBuyNow ?? true,
        enableBuyNowButtonDetector: scriptStatus.config.enableBuyNowButtonDetector ?? true,
        enableCheckoutButtonDetector: scriptStatus.config.enableCheckoutButtonDetector ?? true,
        enableKachingBundle: scriptStatus.config.enableKachingBundle ?? true,
        enableShopifyCartPreloader: scriptStatus.config.enableShopifyCartPreloader ?? false,
        shopifyCheckoutSplitRatio: scriptStatus.config.shopifyCheckoutSplitRatio ?? 100,
        buyNowButtonDetectorUrlPatterns: (scriptStatus.config.buyNowButtonDetectorUrlPatterns || []).join(
          '\n',
        ),
        buyNowButtonDetectorExcludeUrlPatterns: (
          scriptStatus.config.buyNowButtonDetectorExcludeUrlPatterns || []
        ).join('\n'),
        checkoutButtonDetectorExcludeUrlPatterns: (
          scriptStatus.config.checkoutButtonDetectorExcludeUrlPatterns || []
        ).join('\n'),
      });
      setHasUnsavedChanges(false);
    }
  }, [scriptStatus]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchSelectors();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetchSelectors]);

  const renderStatusButton = () => {
    if (!isInjected) {
      return (
        <Button
          size="sm"
          onClick={handleSaveChanges}
          disabled={isLoading}
          className={cn(
            'h-8 shrink-0 px-3 text-xs transition-all',
            installHighlighted && 'animate-bounce ring-2 ring-primary ring-offset-2 shadow-lg',
          )}
        >
          {updateScriptMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Install'}
        </Button>
      );
    }

    if (hasUnsavedChanges || hasNewSelectors) {
      return (
        <Button
          size="sm"
          onClick={handleSaveChanges}
          disabled={isLoading}
          className="h-8 shrink-0 px-3 text-xs"
        >
          {updateScriptMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={handleRemoveScript}
        disabled={isLoading}
        className="h-8 shrink-0 px-3 text-xs text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
      >
        {removeScriptMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Remove'}
      </Button>
    );
  };

  const renderFeatureToggle = (
    field: keyof ConfigFormData,
    label: string,
    description: string,
    disabled = false,
  ) => (
    <div
      className={`flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700 ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="flex-1">
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-slate-600 dark:text-slate-400">{description}</p>
      </div>
      <Switch
        checked={config[field] as boolean}
        onCheckedChange={(checked) => handleConfigChange(field, checked)}
        disabled={disabled}
      />
    </div>
  );

  const renderTextarea = (
    field: keyof ConfigFormData,
    label: string,
    description: string,
    placeholder: string,
    enabledField: keyof ConfigFormData,
  ) => {
    const isEnabled = config[enabledField] as boolean;
    return (
      <div
        className={`rounded-lg border p-3 ${isEnabled
          ? 'border-slate-200 dark:border-slate-700'
          : 'border-slate-200 bg-slate-50 opacity-60 dark:border-slate-700 dark:bg-slate-800/50'
          }`}
      >
        <Label className={`text-sm font-medium ${!isEnabled && 'text-slate-400 dark:text-slate-500'}`}>
          {label}
        </Label>
        <p
          className={`mb-2 text-xs ${isEnabled ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}
        >
          {description}
        </p>
        <textarea
          value={config[field] as string}
          onChange={(e) => handleConfigChange(field, e.target.value)}
          disabled={!isEnabled}
          className="min-h-[80px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:disabled:bg-slate-900/50"
          placeholder={
            isEnabled ? placeholder : `Enable ${label.split(' ')[0]} detector to configure patterns`
          }
        />
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* ─── Header (matches StepEditorPanel) ──────────────────────── */}
      <div className="sticky top-0 z-40 border-b bg-white dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center gap-1.5 px-3 py-2">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
            title="Close"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <Input
            value={step.label}
            onChange={(e) => onChange({ ...step, label: e.target.value })}
            placeholder="Page name..."
            className="h-7 min-w-0 flex-1 border-transparent bg-transparent px-1 text-sm font-semibold shadow-none focus-visible:border-slate-200 focus-visible:bg-white dark:focus-visible:border-slate-700 dark:focus-visible:bg-slate-900"
          />
          <Select
            value={step.type}
            onValueChange={(value: StepType) => onChange({ ...step, type: value })}
          >
            <SelectTrigger className="h-7 w-auto shrink-0 gap-1 border-none bg-slate-50 px-2 text-[11px] font-medium text-slate-500 shadow-none hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 [&_svg]:size-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Page Types</SelectLabel>
                {(Object.entries(STEP_TYPE_LABELS) as [StepType, string][]).map(
                  ([type, label]) => {
                    const TypeIcon = STEP_ICONS[type];
                    const isShopifyStep = type.includes('shopify');
                    const isWooStep = type.includes('woocommerce');
                    const isDisabled =
                      (isShopifyStep && !shopifyAvailable) ||
                      (isWooStep && !wooAvailable);
                    return (
                      <SelectItem key={type} value={type} disabled={isDisabled}>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="h-3.5 w-3.5" />
                          <span className={isDisabled ? 'opacity-50' : ''}>{label}</span>
                        </div>
                      </SelectItem>
                    );
                  }
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ─── Scrollable Content ──────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">

        {/* ─── Connection Status (always visible, top section) ──── */}
        <div className="border-b border-slate-200 p-4 dark:border-slate-700">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Shopify Integration
            </h3>
            {isCheckingScript && (
              <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
            )}
          </div>

          {isCheckingScript ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : scriptStatus ? (
            <div
              ref={installSectionRef}
              className={cn(
                'rounded-lg border bg-white transition-all duration-300 dark:bg-slate-800/50',
                installHighlighted
                  ? 'border-primary ring-2 ring-primary/40 shadow-[0_0_20px_rgba(var(--primary-rgb,99,102,241),0.3)]'
                  : 'border-slate-200 dark:border-slate-700',
              )}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isInjected
                    ? 'bg-emerald-500/10 dark:bg-emerald-500/20'
                    : 'bg-slate-100 dark:bg-slate-700'
                  }`}
                >
                  {isInjected ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {isInjected ? 'Connected' : 'Not connected'}
                    </span>
                    {isInjected && (
                      <span className="inline-flex h-5 items-center rounded-full bg-emerald-500/10 px-2 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                        Live
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {isInjected && scriptStatus.shopUrl ? scriptStatus.shopUrl : 'Configure and install'}
                  </p>
                </div>
                {renderStatusButton()}
              </div>

              {hasNewSelectors && !hasUnsavedChanges && (
                <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/50 px-4 py-2 dark:border-slate-700 dark:bg-slate-800/30">
                  <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  <span className="text-xs text-slate-600 dark:text-slate-400">
                    {pendingSelectorCount} pending selector{pendingSelectorCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* Escape hatch: always allow removing the injected script,
                  even when Save/Install is the primary action. Avoids
                  merchants being locked out of removal when the parser
                  reports false-positive pending state. */}
              {isInjected && (hasUnsavedChanges || hasNewSelectors) && (
                <div className="flex items-center justify-end border-t border-slate-100 px-4 py-1.5 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={handleRemoveScript}
                    disabled={isLoading}
                    className="text-[11px] text-slate-400 underline-offset-2 transition-colors hover:text-red-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-500 dark:hover:text-red-400"
                  >
                    {removeScriptMutation.isPending ? 'Removing…' : 'Remove integration script'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 px-6 py-8 text-center dark:border-slate-600 dark:bg-slate-800/30">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
                <Store className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Shopify not connected</p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Connect your Shopify store to configure this step</p>
              </div>
              <Link to={storeRoutes.INTEGRATION_CONFIG('shopify')}>
                <Button size="sm" className="gap-2">
                  <Store className="h-3.5 w-3.5" />
                  Connect Shopify
                </Button>
              </Link>
            </div>
          )}

          {/* Conflict Warning */}
          {scriptStatus?.isConflict && scriptStatus.conflictDetails && (
            <Alert className="mt-3 border-amber-500 bg-amber-50 dark:border-amber-600 dark:bg-amber-950">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <AlertDescription className="text-sm text-amber-900 dark:text-amber-100">
                {scriptStatus.conflictDetails.currentStoreId ? (
                  <>
                    <p className="font-semibold">Installed for another store</p>
                    <p className="mt-1 text-xs">
                      The script in this theme belongs to store {scriptStatus.conflictDetails.currentStoreId}, so
                      checkouts don't reach this store. Installing will replace it.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">Configuration Conflict</p>
                    <p className="mt-1 text-xs">
                      A script is already installed for a different funnel/step. Installing will replace it.
                    </p>
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Theme Audit — surfaces theme-level conflicts that silently
              bypass or break the integration (custom checkout buttons,
              external redirects, fetch interceptors, zombie scripts...). */}
          {scriptStatus?.isInjected && themeAudit && !themeAudit.isHealthy && (
            <Alert
              className={cn(
                'mt-3',
                themeAudit.summary.high > 0
                  ? 'border-red-500 bg-red-50 dark:border-red-600 dark:bg-red-950'
                  : 'border-amber-500 bg-amber-50 dark:border-amber-600 dark:bg-amber-950',
              )}
            >
              <ShieldAlert
                className={cn(
                  'h-4 w-4',
                  themeAudit.summary.high > 0
                    ? 'text-red-600 dark:text-red-500'
                    : 'text-amber-600 dark:text-amber-500',
                )}
              />
              <AlertDescription
                className={cn(
                  'text-sm',
                  themeAudit.summary.high > 0
                    ? 'text-red-900 dark:text-red-100'
                    : 'text-amber-900 dark:text-amber-100',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">
                      {themeAudit.summary.high > 0
                        ? 'Theme conflicts detected — checkout may be broken'
                        : 'Theme review recommended'}
                    </p>
                    <p className="mt-1 text-xs">
                      {themeAudit.findings.length} potential conflict
                      {themeAudit.findings.length !== 1 ? 's' : ''} found
                      {themeAudit.summary.high > 0 && ` (${themeAudit.summary.high} critical)`}
                      {themeAudit.summary.medium > 0 && `, ${themeAudit.summary.medium} medium`}
                      {themeAudit.summary.low > 0 && `, ${themeAudit.summary.low} low`}.
                      {' '}
                      Custom theme code may be intercepting checkout clicks before TagadaPay can take over.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => refetchAudit()}
                    disabled={isAuditFetching}
                    className="h-6 shrink-0 px-2 text-[11px]"
                    title="Re-scan theme"
                  >
                    <RefreshCw className={cn('h-3 w-3', isAuditFetching && 'animate-spin')} />
                  </Button>
                </div>

                {auditExpanded && (
                  <ul className="mt-3 space-y-2">
                    {themeAudit.findings.slice(0, 8).map((f, idx) => (
                      <li
                        key={`${f.ruleId}-${f.file}-${f.line}-${idx}`}
                        className="rounded border border-current/20 bg-white/50 p-2 dark:bg-black/20"
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={cn(
                              'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                              f.severity === 'high'
                                ? 'bg-red-600 text-white'
                                : f.severity === 'medium'
                                  ? 'bg-amber-600 text-white'
                                  : 'bg-slate-500 text-white',
                            )}
                          >
                            {f.severity}
                          </span>
                          <code className="truncate text-[10px] text-slate-700 dark:text-slate-300">
                            {f.file}
                            {f.line ? `:${f.line}` : ''}
                          </code>
                        </div>
                        <p className="mt-1 text-xs">{f.message}</p>
                        <p className="mt-1 text-[11px] opacity-80">{f.recommendation}</p>
                      </li>
                    ))}
                    {themeAudit.findings.length > 8 && (
                      <li className="text-[11px] opacity-70">
                        + {themeAudit.findings.length - 8} more issue
                        {themeAudit.findings.length - 8 !== 1 ? 's' : ''} not shown.
                      </li>
                    )}
                  </ul>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAuditExpanded((v) => !v)}
                    className="text-[11px] underline-offset-2 hover:underline"
                  >
                    {auditExpanded ? 'Hide details' : 'Show details'}
                  </button>
                  {brandingConfig.supportEmail && (
                    <a
                      href={`mailto:${brandingConfig.supportEmail}?subject=Shopify%20theme%20conflict%20detected`}
                      className="inline-flex items-center gap-1 rounded-md bg-current/10 px-2 py-1 text-[11px] font-medium hover:bg-current/15"
                    >
                      <LifeBuoy className="h-3 w-3" />
                      Contact support
                    </a>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {scriptStatus?.isInjected && isAuditing && !themeAudit && (
            <div className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Scanning theme for conflicts…
            </div>
          )}

          {/* Preview link */}
          {onPreview && scriptStatus?.shopUrl && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full gap-1.5 text-xs"
              onClick={onPreview}
            >
              <Store className="h-3.5 w-3.5" />
              Preview in Shopify
            </Button>
          )}
        </div>

        {shopifyAvailable && (
          <>
            {/* ─── Settings ──────────────────────────────────────────── */}
            <SectionGroupHeader title="Configuration" />

        {/* Core Features */}
        <CollapsibleSection
          title="Core Features"
          icon={Zap}
          defaultOpen={true}
        >
          <div className="space-y-3">
            {renderFeatureToggle(
              'enableGenericBuyNow',
              'Generic Buy Now',
              'Enable generic "Buy Now" button functionality',
            )}
            {renderFeatureToggle(
              'enableKachingBundle',
              'Kaching Bundle',
              'Enable Kaching bundle app integration',
            )}
          </div>
        </CollapsibleSection>

        {/* Button Detection */}
        <CollapsibleSection
          title="Button Detection"
          icon={MousePointer2}
          defaultOpen={false}
        >
          <div className="space-y-3">
            {renderFeatureToggle(
              'enableBuyNowButtonDetector',
              'Buy Now Button Detector',
              'Automatically detect and intercept "Buy Now" buttons',
            )}
            {renderFeatureToggle(
              'enableCheckoutButtonDetector',
              'Checkout Button Detector',
              'Automatically detect and intercept checkout buttons',
            )}
          </div>
        </CollapsibleSection>

        {/* Selectors & Split */}
        <CollapsibleSection
          title="Advanced Settings"
          icon={Settings2}
          badge={databaseSelectors && databaseSelectors.length > 0 ? `${databaseSelectors.length} selector${databaseSelectors.length > 1 ? 's' : ''}` : undefined}
          badgeVariant="info"
          defaultOpen={false}
        >
          <div className="space-y-3">
            {renderFeatureToggle(
              'enableSelector',
              'Hooking Button Selectors',
              'Use the buttons picked on your store as soon as they are saved',
            )}

            <ShopifyPickButtonLink storeId={storeId} isCustomButtonsOn={config.enableSelector} />

            {/* Mapped Selectors */}
            {databaseSelectors && databaseSelectors.length > 0 && (
              <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 dark:border-primary/30 dark:bg-primary/10">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MousePointer2 className="h-4 w-4 text-primary" />
                    <Label className="text-sm font-medium text-primary">
                      Mapped Selectors ({databaseSelectors.length})
                    </Label>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => refetchSelectors()}
                    disabled={isLoadingSelectors}
                    className="h-7 gap-1.5 px-2 text-primary hover:bg-primary/15 hover:text-primary dark:text-primary dark:hover:bg-primary/15"
                    title="Refresh selectors"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${isLoadingSelectors ? 'animate-spin' : ''}`} />
                    <span className="text-xs">Refresh</span>
                  </Button>
                </div>
                <p className="mb-3 text-xs text-primary">
                  Elements mapped using the browser widget. Click "Save" to inject new selectors.
                </p>
                <div className="space-y-2">
                  {databaseSelectors.map((selector: Selector, idx: number) => {
                    const selectorIsInjected = scriptStatus?.config?.selectors?.some(
                      (s) => s.xpath === selector.xpath,
                    );
                    return (
                      <div
                        key={idx}
                        className={`rounded border p-2 ${selectorIsInjected
                          ? 'border-emerald-300 bg-white dark:border-emerald-700 dark:bg-emerald-900/30'
                          : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="inline-flex shrink-0 items-center rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                                {selector.label}
                              </span>
                              {Boolean(selector.config) && (
                                <span className="inline-flex shrink-0 items-center rounded bg-purple-600 px-1.5 py-0.5 text-[10px] font-semibold text-white dark:bg-purple-500">
                                  ENHANCED
                                </span>
                              )}
                              {selectorIsInjected ? (
                                <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white dark:bg-emerald-500">
                                  <CheckCircle2 className="h-2.5 w-2.5" />
                                  INJECTED
                                </span>
                              ) : (
                                <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-amber-600 px-1.5 py-0.5 text-[10px] font-semibold text-white dark:bg-amber-500">
                                  <AlertCircle className="h-2.5 w-2.5" />
                                  NEW
                                </span>
                              )}
                            </div>
                            <p className="mt-1.5 break-all font-mono text-[10px] leading-relaxed text-slate-600 dark:text-slate-400">
                              {selector.xpath.length > 60
                                ? `...${selector.xpath.slice(-60)}`
                                : selector.xpath}
                            </p>
                            {(() => {
                              const cfg = selector.config as { items?: unknown[] } | null;
                              if (cfg && typeof cfg === 'object' && Array.isArray(cfg.items)) {
                                return (
                                  <p className="mt-1 text-[10px] text-purple-700 dark:text-purple-300">
                                    {cfg.items.length} product variant(s)
                                  </p>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSelector(selector.xpath)}
                            disabled={deleteSelectorMutation.isPending}
                            className="h-6 w-6 shrink-0 p-0 text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                            title="Delete selector"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isLoadingSelectors && (
              <div className="flex items-center justify-center rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                <span className="ml-2 text-xs text-slate-500">Loading mapped selectors...</span>
              </div>
            )}

            <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <Label className="text-sm font-medium">Checkout Split Ratio (%)</Label>
              <p className="mb-2 text-xs text-slate-600 dark:text-slate-400">
                Percentage of traffic redirected to TagadaPay checkout
              </p>
              <Input
                type="number"
                min="0"
                max="100"
                value={config.shopifyCheckoutSplitRatio}
                onChange={(e) =>
                  handleConfigChange('shopifyCheckoutSplitRatio', Number(e.target.value))
                }
                className="w-32"
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* ─── Advanced ──────────────────────────────────────────── */}
        <SectionGroupHeader title="Advanced" />

        {/* URL Patterns */}
        <CollapsibleSection
          title="URL Patterns"
          icon={LinkIcon}
          description="Include/exclude pages for button detection"
          defaultOpen={false}
        >
          <div className="space-y-3">
            {renderTextarea(
              'buyNowButtonDetectorUrlPatterns',
              'Buy Now Button URL Patterns',
              'URLs where Buy Now buttons should be detected (one per line)',
              '/products/*\n/collections/*',
              'enableBuyNowButtonDetector',
            )}
            {renderTextarea(
              'buyNowButtonDetectorExcludeUrlPatterns',
              'Buy Now Button Exclude Patterns',
              'URLs to exclude from Buy Now detection (one per line)',
              '/admin/*\n/account/*',
              'enableBuyNowButtonDetector',
            )}
            {renderTextarea(
              'checkoutButtonDetectorExcludeUrlPatterns',
              'Checkout Button Exclude Patterns',
              'URLs to exclude from checkout detection (one per line)',
              '/cart/discount/*',
              'enableCheckoutButtonDetector',
            )}
          </div>
        </CollapsibleSection>
          </>
        )}
      </div>

      {/* ─── Footer ──────────────────────────────────────────────── */}
      <div className="border-t bg-slate-50 px-4 py-1.5 text-center dark:border-slate-700 dark:bg-slate-900">
        <p className="font-mono text-[9px] text-slate-400 dark:text-slate-600">Step ID: {step.id}</p>
      </div>
    </div>
  );
}
